/**
 * SSO Service for Office.js Single Sign-On
 * 
 * This service handles obtaining Azure AD access tokens using Office.js SSO.
 * It provides seamless authentication using the user's already-logged-in Outlook identity.
 */

export type AuthErrorType = 'AUTH_FAILED' | 'FORBIDDEN' | 'EXPIRED' | 'NETWORK' | 'USER_CANCELLED' | 'CONFIG_ERROR';

export interface AuthError {
  message: string;
  type: AuthErrorType;
  code?: number | string;
}

interface SSOToken {
  accessToken: string;
  expiresOn: Date;
}

class SSOService {
  private static instance: SSOService;
  private cachedToken: SSOToken | null = null;
  private tokenAcquisitionInProgress = false;

  private constructor() {}

  /**
   * Get singleton instance
   */
  public static getInstance(): SSOService {
    if (!SSOService.instance) {
      SSOService.instance = new SSOService();
    }
    return SSOService.instance;
  }

  /**
   * Get Azure AD access token using Office.js SSO
   * 
   * This method:
   * 1. Checks if we have a valid cached token
   * 2. If not, attempts to get token via Office.auth.getAccessToken()
   * 3. If SSO fails, falls back to dialog authentication
   * 4. Caches the token for reuse
   * 
   * @param forceRefresh - Force getting a new token even if cached one exists
   * @returns Promise<string> - The access token
   * @throws Error if token acquisition fails
   */
  public async getAccessToken(forceRefresh = false): Promise<string> {
    try {
      // Return cached token if valid and not forcing refresh
      if (!forceRefresh && this.cachedToken && this.isTokenValid(this.cachedToken)) {
        return this.cachedToken.accessToken;
      }

      // Prevent multiple simultaneous token acquisitions
      if (this.tokenAcquisitionInProgress) {
        await this.waitForTokenAcquisition();
        if (this.cachedToken && this.isTokenValid(this.cachedToken)) {
          return this.cachedToken.accessToken;
        }
      }

      this.tokenAcquisitionInProgress = true;

      
      // Try Office.js SSO
      const token = await this.getSSOToken();
      this.cacheToken(token);
      this.tokenAcquisitionInProgress = false;
      return token;
    } catch (error: any) {
      this.tokenAcquisitionInProgress = false;
      console.error('Failed to acquire Azure AD token:', error);
      
      // Provide structured error with type
      const structuredError = this.getStructuredError(error);
      const errorWithDetails = new Error(structuredError.message) as any;
      errorWithDetails.type = structuredError.type;
      errorWithDetails.code = structuredError.code;
      throw errorWithDetails;
    }
  }

  /**
   * Get token using Office.js getAccessToken (primary method)
   */
  private async getSSOToken(): Promise<string> {
    return new Promise((resolve, reject) => {
      if (!Office.auth) {
        reject(new Error('Office.auth not available. Office.js might not be loaded.'));
        return;
      }

      Office.auth.getAccessToken({
        allowSignInPrompt: true,
        allowConsentPrompt: true,
        forMSGraphAccess: false, // We're getting token for our own API
      })
        .then((token: string) => {
          if (!token) {
            reject(new Error('No token returned from Office.auth.getAccessToken'));
            return;
          }
          resolve(token);
        })
        .catch((error: any) => {
          // Pass through the original error to preserve the error code
          // getHelpfulErrorMessage() will provide user-friendly messages
          reject(error);
        });
    });
  }

  /**
   * Get structured error with type based on SSO error code
   */
  private getStructuredError(error: any): AuthError {
    const errorCode = error.code || error;
    const errorMessage = error.message || '';
    
    // Check for specific error codes
    if (errorCode === 13005 || errorMessage.includes('manifest configuration error')) {
      return {
        message: 'Azure AD SSO configuration incomplete. Please complete Steps 1-5 in AZURE_AD_QUICK_START.md:\n' +
               '1. Set Application ID URI\n' +
               '2. Add access_as_user scope\n' +
               '3. Authorize Outlook client applications\n' +
               '4. Configure redirect URIs\n' +
               '5. Grant admin consent',
        type: 'CONFIG_ERROR',
        code: errorCode
      };
    }
    
    if (errorCode === 13007 || errorMessage.includes('Admin consent required')) {
      return {
        message: 'Administrator consent required. Go to Azure Portal → Your App Registration → API permissions → Grant admin consent for your organization.',
        type: 'FORBIDDEN',
        code: errorCode
      };
    }
    
    if (errorCode === 13006 || errorMessage.includes('not from the authorized organization')) {
      return {
        message: 'This add-in is only available for members of the authorized organization.',
        type: 'FORBIDDEN',
        code: errorCode
      };
    }
    
    if (errorCode === 13002 || errorMessage.includes('User cancelled')) {
      return {
        message: 'Sign-in was cancelled. Please try again and complete the sign-in process.',
        type: 'USER_CANCELLED',
        code: errorCode
      };
    }
    
    if (errorCode === 13001 || errorMessage.includes('not signed in')) {
      return {
        message: 'You are not signed in to Office. Please sign in and try again.',
        type: 'AUTH_FAILED',
        code: errorCode
      };
    }
    
    if (errorCode === 13009 || errorMessage.includes('Network error')) {
      return {
        message: 'Network error during authentication. Please check your internet connection and try again.',
        type: 'NETWORK',
        code: errorCode
      };
    }

    if (errorMessage.includes('expired') || errorMessage.includes('exp')) {
      return {
        message: 'Your session has expired. Please sign in again.',
        type: 'EXPIRED',
        code: errorCode
      };
    }
    
    // Default error with reference to documentation
    return {
      message: `Authentication failed: ${errorMessage}. Please ensure Azure AD is properly configured (see AZURE_AD_QUICK_START.md).`,
      type: 'AUTH_FAILED',
      code: errorCode
    };
  }  /**
   * Check if cached token is still valid
   */
  private isTokenValid(token: SSOToken): boolean {
    // Consider token invalid if it expires in less than 5 minutes
    const bufferTime = 5 * 60 * 1000; // 5 minutes in milliseconds
    return token.expiresOn.getTime() - Date.now() > bufferTime;
  }

  /**
   * Cache token with expiration time
   */
  private cacheToken(accessToken: string): void {
    // Decode JWT to get expiration (tokens are JWTs)
    try {
      const tokenParts = accessToken.split('.');
      if (tokenParts.length !== 3) {
        throw new Error('Invalid JWT format');
      }

      const payload = JSON.parse(atob(tokenParts[1]));
      const expiresOn = new Date(payload.exp * 1000); // exp is in seconds

      this.cachedToken = {
        accessToken,
        expiresOn
      };

      // Also store in localStorage as backup
      localStorage.setItem('azure_ad_token', accessToken);
      localStorage.setItem('azure_ad_token_expires', expiresOn.toISOString());

    } catch (error) {
      console.error('Failed to parse token expiration:', error);
      // Cache anyway with 1 hour expiration
      this.cachedToken = {
        accessToken,
        expiresOn: new Date(Date.now() + 60 * 60 * 1000)
      };
    }
  }

  /**
   * Wait for ongoing token acquisition to complete
   */
  private async waitForTokenAcquisition(): Promise<void> {
    const maxWaitTime = 30000; // 30 seconds
    const checkInterval = 500; // Check every 500ms
    let waited = 0;

    return new Promise((resolve) => {
      const interval = setInterval(() => {
        waited += checkInterval;
        if (!this.tokenAcquisitionInProgress || waited >= maxWaitTime) {
          clearInterval(interval);
          resolve();
        }
      }, checkInterval);
    });
  }

  /**
   * Clear cached token (useful for logout or token refresh)
   */
  public clearToken(): void {
    this.cachedToken = null;
    localStorage.removeItem('azure_ad_token');
    localStorage.removeItem('azure_ad_token_expires');
  }

  /**
   * Get cached token from localStorage (for app initialization)
   */
  public getCachedToken(): string | null {
    const token = localStorage.getItem('azure_ad_token');
    const expires = localStorage.getItem('azure_ad_token_expires');

    if (!token || !expires) {
      return null;
    }

    // Check if token is still valid
    const expiresOn = new Date(expires);
    const bufferTime = 5 * 60 * 1000; // 5 minutes
    
    if (expiresOn.getTime() - Date.now() < bufferTime) {
      this.clearToken();
      return null;
    }

    // Restore to cache
    this.cachedToken = {
      accessToken: token,
      expiresOn
    };

    return token;
  }

  /**
   * Decode token to get user info (without verification)
   * Note: This is for display purposes only. Real validation happens on backend.
   */
  public decodeToken(token: string): any {
    try {
      const tokenParts = token.split('.');
      if (tokenParts.length !== 3) {
        return null;
      }
      return JSON.parse(atob(tokenParts[1]));
    } catch (error) {
      console.error('Failed to decode token:', error);
      return null;
    }
  }
}

// Export singleton instance
export default SSOService.getInstance();
